<template>
  <div class="profiles-wrapper">
    <div class="profiles-grid">
      <div
        v-for="city in sortedCities"
        :key="city.id"
        class="profile-card"
        :style="{ borderTopColor: getColor(city) }"
        @click="$emit('select-city', city.id)"
      >
        <div class="profile-name">{{ city.label }}</div>
        <div class="profile-rows">
          <div class="profile-row">
            <span class="profile-row-label">Inflöde</span>
            <span class="profile-row-value">{{ city.incoming }}</span>
          </div>
          <div class="profile-row">
            <span class="profile-row-label">Utflöde</span>
            <span class="profile-row-value">{{ city.outgoing }}</span>
          </div>
          <div class="profile-row">
            <span class="profile-row-label">Nettoflytt</span>
            <span
              class="profile-row-value"
              :class="{
                positive: net(city) > 0,
                negative: net(city) < 0
              }"
            >
              {{ net(city) > 0 ? '+' : '' }}{{ net(city) }}
            </span>
          </div>
          <div v-if="city.internal" class="profile-row">
            <span class="profile-row-label">Internt</span>
            <span class="profile-row-value internal">
              {{ city.internal }}
            </span>
          </div>
        </div>

        <div v-if="topDestinations(city).length" class="profile-destinations">
          <div class="profile-dest-title">Flyttar till</div>
          <div
            v-for="dest in topDestinations(city)"
            :key="dest.label"
            class="profile-dest-row"
          >
            <span
              class="profile-dest-dot"
              :style="{ backgroundColor: dest.color }"
            ></span>
            <span class="profile-dest-label">{{ dest.label }}</span>
            <span class="profile-dest-count">{{ dest.count }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const PALETTE = {
  green: '#3A6B44',
  teal: '#2A7B6F',
  rose: '#B84040'
};

export default {
  props: {
    nodes: { type: Array, required: true },
    rawFlows: { type: Array, required: true },
    colorMap: { type: Object, required: true }
  },
  emits: ['select-city'],
  computed: {
    sortedCities() {
      return [...this.nodes].sort((a, b) => {
        const totalA = a.outgoing + a.incoming + (a.internal || 0);
        const totalB = b.outgoing + b.incoming + (b.internal || 0);
        return totalB - totalA;
      });
    }
  },
  methods: {
    net(city) {
      return city.incoming - city.outgoing;
    },

    getColor(city) {
      const n = this.net(city);
      if (n > 2) return PALETTE.green;
      if (n < -2) return PALETTE.rose;
      return PALETTE.teal;
    },

    topDestinations(city) {
      return this.rawFlows
        .filter(f => f.from === city.id)
        .map(f => ({
          label: f.to,
          count: f.count,
          color: this.colorMap[f.to] || '#3A5A72'
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);
    }
  }
};
</script>

<style scoped>
.profiles-wrapper {
  padding: 24px 28px;
}

.profiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.profile-card {
  background: #fdfbf7;
  border-radius: 12px;
  border: 1px solid #d6cebf;
  border-top: 3px solid;
  padding: 16px 14px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.profile-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.profile-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1209;
  margin-bottom: 10px;
}

.profile-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.profile-row {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
}

.profile-row-label {
  color: #7a6e5f;
}

.profile-row-value {
  font-weight: 700;
  color: #1a1209;
}

.profile-row-value.positive {
  color: #3a6b44;
}

.profile-row-value.negative {
  color: #b84040;
}

.profile-row-value.internal {
  color: #9a7b2a;
}

.profile-destinations {
  border-top: 1px solid #e8e2d6;
  padding-top: 8px;
}

.profile-dest-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #7a6e5f;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.profile-dest-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  font-size: 1.1rem;
}

.profile-dest-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.profile-dest-label {
  flex: 1;
  color: #1a1209;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-dest-count {
  font-weight: 700;
  color: #1a1209;
  min-width: 20px;
  text-align: right;
}
</style>
